// Client-side game engine for the one-time GUEST trial game only.
// Mirrors the server engine (pricedisplay/backend/bitwheelEngine.js) exactly:
// hit-to-win scoring, stacking multipliers, 10 spins / 10 spin points.
// Guest scores are never persisted or put on the leaderboard, so resolving
// locally is safe — real accounts always play against the server engine.

export const GUEST_CONFIG = {
  spinsPerGame: 10,
  startingSpinPoints: 10,
  maxWager: 2
};

const COLOR_MULTIPLIERS = { green: 7, yellow: 6, blue: 3, orange: 3 };
const NUMBER_MULTIPLIER = 42;

export function segmentFor(number) {
  if (number === 1 || number === 22) {
    return { color: 'green', value: 1000, tier: 'Mega Win' };
  }
  if ([42, 2, 21, 23].includes(number)) {
    return { color: 'green', value: 500, tier: 'Super Win' };
  }
  if ([11, 12, 32, 33].includes(number)) {
    return { color: 'yellow', value: 250, tier: 'Super Tie' };
  }
  if ([10, 13, 31, 34].includes(number)) {
    return { color: 'yellow', value: 200, tier: 'Tie' };
  }
  if ((number >= 3 && number <= 9) || (number >= 35 && number <= 41)) {
    return { color: 'blue', value: 100, tier: 'Standard' };
  }
  return { color: 'orange', value: 100, tier: 'Standard' };
}

// Uniform 1..42 using rejection sampling over crypto random bytes.
export function randomSegmentNumber() {
  if (window.crypto && window.crypto.getRandomValues) {
    const buf = new Uint8Array(1);
    for (;;) {
      window.crypto.getRandomValues(buf);
      // 252 = 6 * 42: reject the tail so every value 1..42 is equally likely.
      if (buf[0] < 252) {
        return (buf[0] % 42) + 1;
      }
    }
  }
  return Math.floor(Math.random() * 42) + 1;
}

// Same resolution rules as the server: the color or the number must be called
// correctly to win; on a hit, payout = value x wager x colorBonus x numberBonus.
export function resolveGuestSpin({ resultNumber, wager, chosenColor, chosenNumber }) {
  const seg = segmentFor(resultNumber);
  const w = Number(wager);

  const colorHit = w >= 1 && !!chosenColor && chosenColor === seg.color;
  const numberHit =
    w >= 1 && chosenNumber !== null && chosenNumber !== undefined && chosenNumber !== '' &&
    Number(chosenNumber) === resultNumber;

  const hit = colorHit || numberHit;
  const colorBonus = colorHit ? COLOR_MULTIPLIERS[seg.color] : 1;
  const numberBonus = numberHit ? NUMBER_MULTIPLIER : 1;
  const multiplier = hit ? w * colorBonus * numberBonus : 0;
  const payout = seg.value * multiplier;

  let matchType;
  if (w === 0) matchType = 'skip';
  else if (numberHit) matchType = 'number';
  else if (colorHit) matchType = 'color';
  else matchType = 'none';

  let message;
  if (w === 0) message = 'Observing this spin — very little risk on a 0 wager!';
  else if (matchType === 'number') message = `Bullseye! Exact number hit — ${payout.toLocaleString()} points on the board!`;
  else if (matchType === 'color') message = `${seg.tier} — color matched for ${payout.toLocaleString()} points!`;
  else message = 'So close! Call the color or the number to bank points — spin again!';

  return { resultSegment: resultNumber, resultColor: seg.color, segmentValue: seg.value, matchType, payout, message };
}

// ----- Guest trial state (one free game per browser; game lives in the tab session) -----

// The trial only locks in when a game is COMPLETED (all 10 spins used). An
// abandoned/interrupted game must never strand a first-time player with zero
// spins — if the tab dies mid-game, a fresh session simply grants a fresh
// trial (guest scores are never persisted, so there is nothing to farm).
const TRIAL_DONE_KEY = 'bitwheel.guestTrialDone';
const GUEST_GAME_KEY = 'bitwheel.guestGame';

export const isTrialUsed = () => {
  try {
    return window.localStorage.getItem(TRIAL_DONE_KEY) === '1';
  } catch (e) {
    return false;
  }
};

export const markTrialDone = () => {
  try {
    window.localStorage.setItem(TRIAL_DONE_KEY, '1');
  } catch (e) { /* storage unavailable — trial simply won't be limited */ }
};

export const newGuestGame = () => ({
  score: 0,
  spinPointsRemaining: GUEST_CONFIG.startingSpinPoints,
  spinsRemaining: GUEST_CONFIG.spinsPerGame,
  bestSpin: 0,
  status: 'active'
});

export const loadGuestGame = () => {
  try {
    const raw = window.sessionStorage.getItem(GUEST_GAME_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.status === 'active' ? parsed : null;
  } catch (e) {
    return null;
  }
};

export const saveGuestGame = (game) => {
  try {
    window.sessionStorage.setItem(GUEST_GAME_KEY, JSON.stringify(game));
  } catch (e) { /* non-fatal */ }
};

export const clearGuestGame = () => {
  try {
    window.sessionStorage.removeItem(GUEST_GAME_KEY);
  } catch (e) { /* non-fatal */ }
};
